import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFTextField } from "../../../lib/formik";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import {
    GetBankBalanceResponseBody,
    GetOrderEntryBanksListResponseBody, GetOrderEntryInvestorListResponseBody
} from "../../../redux-store/types/orderManagement";
import {
    FormikContextType,
    useFormikContext
} from "formik";
import { CurrencyConversionToWords } from "./commonComponents";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { getBankBalance } from "../../../redux-store/actions/orderManagement";
import { showError } from "../../../redux-store/actions/auth";
import {
    Dispatch,
    SetStateAction,
    useRef
} from "react";
import { MPOWER_ACCOUNT_TYPE_MASTER } from "../../../utils/constant";

interface PortfolioDetails {
    portfolioName: string;
    portfolioId: string;
    accountType: string;
    email: string;
}

export default function ClientDetailsForm({
    investorData,
    activeProductType,
    handleClientNameChange,
    bankAccountList,
    handleAccountChange,
    setLoadingStatus,
    isLoading
}: {
    investorData: GetOrderEntryInvestorListResponseBody;
    activeProductType: string;
    handleClientNameChange: (selectedObj: Record<string, any>) => void;
    bankAccountList: GetOrderEntryBanksListResponseBody
    handleAccountChange: (event: SelectChangeEvent<unknown>) => void;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
    isLoading: boolean;
}): JSX.Element {
    const investorBankAccountRef = useRef<HTMLElement>(null)

    const dispatch = useDispatch();

    const { currencySymbol, locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);

    const {
        values,
        resetForm,
        setFieldValue
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const handleInvestorBankAccountChange = async (event: SelectChangeEvent<unknown>) => {
        try {
            setLoadingStatus("apiLoading")

            const selectedInvestorBankAccountObj = (bankAccountList?.bankDetails ?? [])?.find(obj => `${obj?.bankAccNo ?? ""}` === event.target.value)

            await setFieldValue("investorBankAccount", selectedInvestorBankAccountObj ?? null)

            const isAccountTypePoa = MPOWER_ACCOUNT_TYPE_MASTER[(values?.accountType ?? "")?.toUpperCase()] === MPOWER_ACCOUNT_TYPE_MASTER["POA"]

            let getBankBalanceResponse: GetBankBalanceResponseBody | null = null

            if (isAccountTypePoa) {
                getBankBalanceResponse = await (dispatch(getBankBalance({
                    investorBankAccount: event.target.value as string ?? "",
                    clientCode: `${values?.clientName?.clientCode ?? ""}`,
                    investorIfscCode: `${selectedInvestorBankAccountObj?.ifscCode ?? ""}`
                })) as unknown as Promise<GetBankBalanceResponseBody>);

                if (!getBankBalanceResponse?.isSuccess) {
                    dispatch(showError(getBankBalanceResponse?.message ?? "No Data Available"))
                }
            }

            resetForm({
                values: {
                    ...values,
                    investorBankAccount: selectedInvestorBankAccountObj ?? null,
                    availableBankBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.bankBalance ?? 0)) ? Number(getBankBalanceResponse?.bankBalance ?? 0) : 0)?.toLocaleString(locale)
                        : "",
                    utilizedBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.utilizedAmount ?? 0)) ? Number(getBankBalanceResponse?.utilizedAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "",
                    netAvailableBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.netAmount ?? 0)) ? Number(getBankBalanceResponse?.netAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "",
                }
            })
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
            setTimeout(() => {
                investorBankAccountRef.current?.focus()
            }, 0)
        }
    }

    const refactoredAvailableBankBalance = `${values?.availableBankBalance ?? ""}`?.replaceAll(',', '')
    const refactoredUtilizedBalance = `${values?.utilizedBalance ?? ""}`?.replaceAll(',', '')
    const refactoredNetAvailableBalance = `${values?.netAvailableBalance ?? ""}`?.replaceAll(',', '')

    return (
        <Grid
            container
            columnSpacing={3}
        >
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="clientName"
                    label="Client Name *"
                    options={(Array.isArray(investorData?.result) ? investorData?.result : [])?.map((obj) => ({
                        ...obj,
                        label: obj?.customerName,
                        value: obj?.clientCode,
                    })) ?? []}
                    onChange={handleClientNameChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="account"
                    label="Account *"
                    items={(values?.clientName?.portfolios ?? [])?.map((obj: PortfolioDetails) => ({
                        key: `${obj?.portfolioName ?? ""} (${obj?.portfolioId ?? ""})`,
                        value: obj?.portfolioId ?? "",
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleAccountChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="accountType"
                    label="Account Type"
                    placeholder=""
                    disabled={true}
                    value={values?.accountType ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    inputRef={investorBankAccountRef}
                    name="investorBankAccount"
                    label="Investor Bank Account *"
                    items={bankAccountList?.bankDetails?.map(obj => ({
                        key: `${obj?.bankAccNo} ${obj?.bankname}`,
                        value: obj?.bankAccNo,
                    })) ?? []}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleInvestorBankAccountChange}
                    value={`${values?.investorBankAccount?.bankAccNo ?? ""}`}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="emailId"
                    label="Email ID"
                    placeholder=""
                    disabled={true}
                    value={values?.emailId ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="srm"
                    label="RM"
                    items={[]}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="availableBankBalance"
                    label="Available Bank Balance"
                    placeholder=""
                    startAdornment={values?.availableBankBalance ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={true}
                    formatCurrency
                    value={values?.availableBankBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAvailableBankBalance} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="utilizedBalance"
                    label="Utilized Balance"
                    placeholder=""
                    startAdornment={values?.utilizedBalance ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        },
                    }}
                    disabled={true}
                    formatCurrency
                    value={values?.utilizedBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredUtilizedBalance} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="netAvailableBalance"
                    label="Net Available Balance"
                    placeholder=""
                    startAdornment={values?.netAvailableBalance ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={true}
                    formatCurrency
                    value={values?.netAvailableBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredNetAvailableBalance} />
            </Grid>
            {(activeProductType === "Bonds") && (
                <>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="uccCode"
                            label="UCC Code"
                            placeholder=""
                            disabled={true}
                            value={values?.uccCode ?? ""}
                        />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="dpId"
                            label="DP ID"
                            placeholder=""
                            disabled={true}
                            value={values?.dpId ?? ""}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    )
}